import type {
  NavigationCategory,
  NavigationCategoryWithItems,
  NavigationConfigItem,
  NavigationPermission,
} from '@/types/navigation';

export interface ModuleAssignmentInput {
  module_path: string;
  is_enabled: boolean;
}

function toNavigationItem(item: NavigationConfigItem): NavigationPermission {
  return {
    path: item.path,
    name: item.name,
    roles: item.default_roles,
    icon: item.icon_name,
    sortOrder: item.sort_order,
    categoryId: item.category_id,
  };
}

export function filterModulesByAssignments(
  modules: NavigationConfigItem[],
  assignments: ModuleAssignmentInput[],
  organizationId?: string,
): NavigationConfigItem[] {
  const activeGlobalModules = modules
    .filter((module) => module.organization_id === null && module.is_active)
    .sort((a, b) => a.sort_order - b.sort_order);

  if (!organizationId) {
    return activeGlobalModules;
  }

  const assignmentsMap = new Map<string, boolean>();
  assignments.forEach((assignment) => {
    assignmentsMap.set(assignment.module_path, assignment.is_enabled);
  });

  return activeGlobalModules.filter((module) => {
    const assignment = assignmentsMap.get(module.path);
    return assignment === undefined || assignment === true;
  });
}

export function buildNavigationConfig(config: NavigationConfigItem[]): NavigationPermission[] {
  const mainItems = config.filter((item) => !item.parent_path);
  const subItems = config.filter((item) => !!item.parent_path);

  const result = mainItems.map((mainItem) => {
    const navigationItem = toNavigationItem(mainItem);
    const children = subItems.filter((sub) => sub.parent_path === mainItem.path);

    if (children.length > 0) {
      navigationItem.subItems = children
        .sort((a, b) => a.sort_order - b.sort_order)
        .map(toNavigationItem);
    }

    return navigationItem;
  });

  return result.sort((a, b) => a.sortOrder - b.sortOrder);
}

export function buildNavigationConfigByCategories(
  categories: NavigationCategory[],
  config: NavigationConfigItem[],
): NavigationCategoryWithItems[] {
  const activeCategories = categories
    .filter((category) => category.is_active)
    .sort((a, b) => a.sort_order - b.sort_order);

  const mainItems = config.filter((item) => !item.parent_path);
  const subItems = config.filter((item) => !!item.parent_path);
  const categoryMap = new Map<string, NavigationPermission[]>();

  mainItems.forEach((mainItem) => {
    const navigationItem = toNavigationItem(mainItem);
    const children = subItems.filter((sub) => sub.parent_path === mainItem.path);

    if (children.length > 0) {
      navigationItem.subItems = children
        .sort((a, b) => a.sort_order - b.sort_order)
        .map(toNavigationItem);
    }

    const categoryId = mainItem.category_id || 'uncategorized';
    if (!categoryMap.has(categoryId)) {
      categoryMap.set(categoryId, []);
    }
    categoryMap.get(categoryId)?.push(navigationItem);
  });

  categoryMap.forEach((items) => {
    items.sort((a, b) => a.sortOrder - b.sortOrder);
  });

  const result: NavigationCategoryWithItems[] = [];
  activeCategories.forEach((category) => {
    const items = categoryMap.get(category.id);
    if (items?.length) {
      result.push({ category, items });
    }
  });

  const uncategorizedItems = categoryMap.get('uncategorized');
  if (uncategorizedItems?.length) {
    result.push({
      category: {
        id: 'uncategorized',
        name: 'Sin categoría',
        slug: 'uncategorized',
        icon_name: null,
        description: null,
        sort_order: 999,
        is_active: true,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      },
      items: uncategorizedItems,
    });
  }

  return result;
}

export function hasDefaultNavigationAccess(
  navigationConfig: NavigationPermission[],
  path: string,
  role: string,
): boolean {
  const findPathInConfig = (
    items: NavigationPermission[],
    targetPath: string,
  ): NavigationPermission | null => {
    for (const item of items) {
      if (item.path === targetPath) {
        return item;
      }
      if (item.subItems) {
        const found = findPathInConfig(item.subItems, targetPath);
        if (found) return found;
      }
    }
    return null;
  };

  const item = findPathInConfig(navigationConfig, path);
  return item ? item.roles.includes(role) : false;
}
